/**
 * Date Range Picker Mixin for Alpine.js
 * Provides reusable date picker logic for trip editors and similar features
 */

// Fallback month names (used if i18n not available)
const MONTHS_FULL_FALLBACK = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

const MONTHS_SHORT_FALLBACK = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Get localized full month name
 * @param {number} monthIndex - 0-based month index
 * @returns {string} Localized month name
 */
function getMonthFull(monthIndex) {
    if (typeof window !== 'undefined' && window.getLocalizedMonthFull) {
        return window.getLocalizedMonthFull(monthIndex);
    }
    return MONTHS_FULL_FALLBACK[monthIndex];
}

/**
 * Get localized short month name
 * @param {number} monthIndex - 0-based month index
 * @returns {string} Localized short month name
 */
function getMonthShort(monthIndex) {
    if (typeof window !== 'undefined' && window.getLocalizedMonthShort) {
        return window.getLocalizedMonthShort(monthIndex);
    }
    return MONTHS_SHORT_FALLBACK[monthIndex];
}

/**
 * Creates a date picker mixin with state and methods for date range selection
 * @returns {Object} Object containing date picker state and methods to spread into Alpine component
 */
export function createDatePickerMixin() {
    return {
        // Date picker state
        showDatePicker: false,
        datePickerStep: 'start', // 'start' | 'end'
        datePickerMonth: new Date().getMonth(),
        datePickerYear: new Date().getFullYear(),

        /**
         * Get display text for current month and year
         * @returns {string} e.g., "January 2025" or "январь 2025"
         */
        getMonthYearDisplay() {
            return getMonthFull(this.datePickerMonth) + ' ' + this.datePickerYear;
        },

        /**
         * Navigate to previous month
         */
        prevMonth() {
            if (this.datePickerMonth === 0) {
                this.datePickerMonth = 11;
                this.datePickerYear--;
            } else {
                this.datePickerMonth--;
            }
        },

        /**
         * Navigate to next month
         */
        nextMonth() {
            if (this.datePickerMonth === 11) {
                this.datePickerMonth = 0;
                this.datePickerYear++;
            } else {
                this.datePickerMonth++;
            }
        },

        /**
         * Generate calendar days for current month view
         * Requires component to have startDate and endDate properties
         * @returns {Array} Array of day objects for rendering calendar grid
         */
        getCalendarDays() {
            const days = [];
            const firstDay = new Date(this.datePickerYear, this.datePickerMonth, 1);
            const lastDay = new Date(this.datePickerYear, this.datePickerMonth + 1, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Empty cells for days before first of month (for grid alignment)
            for (let i = 0; i < firstDay.getDay(); i++) {
                days.push({ empty: true });
            }

            // Days of month
            for (let d = 1; d <= lastDay.getDate(); d++) {
                const date = new Date(this.datePickerYear, this.datePickerMonth, d);
                const dateStr = this.formatDateISO(date);
                const isPast = date < today;
                const isBeforeStart = this.datePickerStep === 'end' &&
                    this.startDate && dateStr < this.startDate;

                days.push({
                    day: d,
                    dateStr: dateStr,
                    isToday: date.getTime() === today.getTime(),
                    isStart: dateStr === this.startDate,
                    isEnd: dateStr === this.endDate,
                    isInRange: this.startDate && this.endDate &&
                        dateStr > this.startDate && dateStr < this.endDate,
                    disabled: isPast || isBeforeStart
                });
            }

            return days;
        },

        /**
         * Handle date selection
         * @param {string} dateStr - ISO date string (YYYY-MM-DD)
         */
        selectDate(dateStr) {
            if (this.datePickerStep === 'start') {
                this.startDate = dateStr;
                // Clear end date if it's before new start date
                if (this.endDate && this.endDate < dateStr) {
                    this.endDate = '';
                }
                this.datePickerStep = 'end';
            } else {
                this.endDate = dateStr;
                this.showDatePicker = false;
            }
        },

        /**
         * Format a Date object to ISO string (YYYY-MM-DD)
         * @param {Date} date
         * @returns {string}
         */
        formatDateISO(date) {
            return date.getFullYear() + '-' +
                String(date.getMonth() + 1).padStart(2, '0') + '-' +
                String(date.getDate()).padStart(2, '0');
        },

        /**
         * Format an ISO date string to short display format
         * @param {string} dateStr - ISO date string (YYYY-MM-DD)
         * @returns {string} e.g., "Jan 15" or "янв 15"
         */
        formatDateShort(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr + 'T00:00:00');
            return getMonthShort(d.getMonth()) + ' ' + d.getDate();
        },

        /**
         * Reset date picker to current month
         */
        resetDatePicker() {
            const today = new Date();
            this.datePickerMonth = today.getMonth();
            this.datePickerYear = today.getFullYear();
            this.datePickerStep = 'start';
        }
    };
}
